import { useLang } from "@/i18n/LanguageContext";

/**
 * Compact U.S. distributor credibility strip. Sits directly under the hero
 * to anchor instant credibility ("we source through real U.S. distribution
 * channels") before the visitor reaches the first CTA section.
 *
 * Implementation notes
 * - Uses styled text marks instead of bitmap logos: zero added asset weight,
 *   no CLS, no logo-trademark risk, and renders identically on every device.
 * - Phrasing uses "such as" to avoid implying any formal partnership.
 * - Layout is a single low-contrast row; on mobile it wraps gracefully.
 * - No new sections / structural changes elsewhere — purely additive.
 */
const COPY = {
  label: {
    en: "Sourced from leading U.S. distributors such as",
    es: "Abastecido a través de distribuidores líderes en EE. UU. como",
    pt: "Fornecido por distribuidores líderes nos EUA como",
  },
} as const;

// Major U.S. electrical distribution houses we source through. Kept short and
// neutral — no claim of authorized partnership, just network association.
const DISTRIBUTORS = [
  "Sonepar",
  "Rexel",
  "Graybar",
  "Wesco",
  "Border States",
  "CED",
  "Grainger",
  "MSC",
];

const DistributorStrip = () => {
  const { lang } = useLang();
  return (
    <section
      aria-label="U.S. distributor network"
      className="border-b border-border bg-background"
    >
      <div className="container max-w-6xl py-3.5 md:py-5">
        {/* Mobile: label stacked above, names centered & wrap cleanly with
            subtle dot dividers to read as a single line of text rather than
            a list. Desktop: inline row, same dot-separated rhythm. */}
        <div className="flex flex-col items-center gap-2 text-center md:flex-row md:flex-wrap md:justify-center md:gap-x-5 md:gap-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px]">
            {COPY.label[lang]}
          </p>
          <ul className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 md:gap-x-3">
            {DISTRIBUTORS.map((name, i) => (
              <li
                key={name}
                className="flex items-center text-[12px] font-semibold tracking-tight text-muted-foreground sm:text-sm"
                style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
              >
                {i > 0 && (
                  <span
                    aria-hidden="true"
                    className="mr-2 inline-block h-1 w-1 rounded-full bg-muted-foreground/40 md:mr-3"
                  />
                )}
                {name}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default DistributorStrip;